// B2171 — the typed parameters of `sql(text, params)`.
//
// `encodeParams(params)` writes the parameter value P, the ONE shape that the
// WS Query frame (slot 7), the `POST /sql` params body and the embedded ABI
// (`kdb_exec_params`) carry:
//
//   P = Array [ header, Bytes row ]
//
// - `header` is the B607 typed column header: one
//   `Array [String "$n", IntVar code, IntVar param]` per value, in the order
//   `$1`, `$2`, … . The server ignores the name.
// - `row` is ONE typed row (`crates/kinedb-protocol/src/typed_rows.rs`): a
//   NULL bitmap of ceil(n/8) bytes, LSB first, then the tagless cells. A NULL
//   value sets its bit, has no cell, and has the code 0.
//
// The server binds the values into the SQL text at the ingress, with ONE
// binder, so a value is never parsed as SQL: `it's'); DELETE FROM t; --` is a
// string, not a statement.
//
// THE TYPE TABLE. An integer `number` or a `bigint` → Int (2); a fractional
// `number` → Float (3); a `string` → Text (4); a `boolean` → Bool (1); a `Date`
// → Timestamp (6, microseconds); a `Uint8Array` (a `Buffer` too) → Bytes (5); a
// `Float32Array` → Vector (9, param = its length); `null` or `undefined` →
// NULL. Every other value is refused, before anything goes on the wire.
//
// THIS FILE HAS NO IMPORTS, on purpose. `@kinedb/embedded` must not depend on
// `@kinedb/client`, so it carries a VERBATIM copy of this file
// (`sdk/js/embedded/src/params.js`). `sdk/js/embedded/src/params.test.js`
// reads both files and fails when the two copies differ.

/** The content type of a `POST /sql` body that carries params. */
export const SQL_PARAMS_CONTENT_TYPE = 'application/vnd.kinedb.sql-params';

/** The server refuses a header of more than this many entries. */
export const MAX_PARAMS = 65535;

const T_STRING = 0x05;
const T_BYTES = 0x06;
const T_INT_VAR = 0x08;
const T_ARRAY = 0x10;

const CODE_NULL = 0;
const CODE_BOOL = 1;
const CODE_INT = 2;
const CODE_FLOAT = 3;
const CODE_TEXT = 4;
const CODE_BYTES = 5;
const CODE_TIMESTAMP = 6;
const CODE_VECTOR = 9;

const I64_MIN = -(1n << 63n);
const I64_MAX = (1n << 63n) - 1n;

const UTF8 = new TextEncoder();

/** A growing byte buffer: a list of parts, joined once at the end. */
class Out {
  constructor() { this.parts = []; this.length = 0; }
  push(...b) { this.append(Uint8Array.from(b)); }
  append(u8) { this.parts.push(u8); this.length += u8.length; }
  u32be(n) { this.push((n >>> 24) & 0xFF, (n >>> 16) & 0xFF, (n >>> 8) & 0xFF, n & 0xFF); }
  /** An unsigned LEB128 of a non-negative BigInt. */
  uleb(value) {
    let raw = value;
    for (;;) {
      const byte = Number(raw & 0x7Fn);
      raw >>= 7n;
      if (raw > 0n) { this.push(byte | 0x80); } else { this.push(byte); return; }
    }
  }
  /** A signed i64 as zigzag LEB128. */
  zigzag(value) { this.uleb(((value << 1n) ^ (value >> 63n)) & ((1n << 64n) - 1n)); }
  done() {
    const all = new Uint8Array(this.length);
    let at = 0;
    for (const part of this.parts) { all.set(part, at); at += part.length; }
    return all;
  }
}

function refuse(Kind, n, what) {
  return new Kind(`sql params: $${n} ${what}`);
}

/** The type name of a refused value, for the error text. */
function kindOf(value) {
  if (Array.isArray(value)) return 'an array';
  if (ArrayBuffer.isView(value) || value instanceof ArrayBuffer) {
    return `a ${value.constructor?.name ?? 'binary view'}`;
  }
  if (typeof value === 'object') return 'a plain object';
  return `a ${typeof value}`;
}

/**
 * One value → `{ code, param, cell }`. `cell` writes the tagless cell, and a
 * NULL has no cell. Throws a TypeError or a RangeError for a value outside
 * the type table.
 */
function typeOf(value, n) {
  if (value === null || value === undefined) return { code: CODE_NULL, param: 0, cell: null };
  switch (typeof value) {
    case 'boolean':
      return { code: CODE_BOOL, param: 0, cell: (out) => out.push(value ? 1 : 0) };
    case 'bigint':
      if (value < I64_MIN || value > I64_MAX) {
        throw refuse(RangeError, n, `is ${value}n, outside the i64 range of an INT`);
      }
      return { code: CODE_INT, param: 0, cell: (out) => out.zigzag(value) };
    case 'number': {
      if (!Number.isFinite(value)) {
        throw refuse(RangeError, n, `is ${value}: a FLOAT must be finite`);
      }
      if (Number.isInteger(value)) {
        if (!Number.isSafeInteger(value)) {
          throw refuse(RangeError, n,
            `is ${value}, an integer number past ±(2^53-1) that lost its low bits: pass it as a bigint`);
        }
        const big = BigInt(value);
        return { code: CODE_INT, param: 0, cell: (out) => out.zigzag(big) };
      }
      return {
        code: CODE_FLOAT,
        param: 0,
        cell: (out) => {
          const b = new Uint8Array(8);
          new DataView(b.buffer).setFloat64(0, value, true);
          out.append(b);
        },
      };
    }
    case 'string': {
      const text = UTF8.encode(value);
      return {
        code: CODE_TEXT,
        param: 0,
        cell: (out) => { out.uleb(BigInt(text.length)); out.append(text); },
      };
    }
    case 'object':
      break;
    default:
      throw refuse(TypeError, n, `is ${kindOf(value)}: no SQL type takes it`);
  }
  if (value instanceof Date) {
    const ms = value.getTime();
    if (!Number.isFinite(ms)) throw refuse(RangeError, n, 'is an invalid Date');
    const micros = BigInt(ms) * 1000n;
    return { code: CODE_TIMESTAMP, param: 0, cell: (out) => out.zigzag(micros) };
  }
  // A Float32Array before any other view: it is the one vector type.
  if (value instanceof Float32Array) {
    for (const f of value) {
      if (!Number.isFinite(f)) throw refuse(RangeError, n, 'is a vector with a value that is not finite');
    }
    return {
      code: CODE_VECTOR,
      param: value.length,
      cell: (out) => {
        const b = new Uint8Array(value.length * 4);
        const view = new DataView(b.buffer);
        value.forEach((f, i) => view.setFloat32(i * 4, f, true));
        out.append(b);
      },
    };
  }
  // A Buffer is a Uint8Array, so it is Bytes too.
  if (value instanceof Uint8Array) {
    return {
      code: CODE_BYTES,
      param: 0,
      cell: (out) => { out.uleb(BigInt(value.length)); out.append(value); },
    };
  }
  throw refuse(TypeError, n,
    `is ${kindOf(value)}: no SQL type takes it (pass a Uint8Array for BYTES, a Float32Array for a VECTOR)`);
}

function tlvString(out, text) {
  const b = UTF8.encode(text);
  out.push(T_STRING);
  out.u32be(b.length);
  out.append(b);
}

function tlvIntVar(out, value) {
  out.push(T_INT_VAR);
  out.zigzag(BigInt(value));
}

/**
 * The TLV bytes of P for `params`, or `null` for no params (`undefined`,
 * `null` or an empty array): a call with no params keeps the old bytes.
 *
 * @param {Array|null|undefined} params the values of `$1`, `$2`, … in order.
 * @returns {Uint8Array|null}
 * @throws {TypeError|RangeError} for a value outside the type table. The
 *   message names the position (`$n`).
 */
export function encodeParams(params) {
  if (params === undefined || params === null) return null;
  if (!Array.isArray(params)) {
    throw new TypeError(`sql params: pass the values as an array, not ${kindOf(params)}`);
  }
  if (params.length === 0) return null;
  return encodeTyped(typesOf(params));
}

/** The type of each value of a non-empty array, after the count check. */
function typesOf(params) {
  if (params.length > MAX_PARAMS) {
    throw new RangeError(`sql params: ${params.length} values, more than the limit of ${MAX_PARAMS}`);
  }
  return params.map((value, i) => typeOf(value, i + 1));
}

/**
 * The `POST /sql` params body: the TLV `Array [String sql, P]`. Answers `null`
 * for no params; the caller then sends the plain-text body.
 */
export function encodeSqlParamsBody(sql, params) {
  return sqlParamsBodyOf(sql, encodeParams(params));
}

/**
 * B2173: the `POST /sql` params body for a P that is already encoded, or
 * `null` for `p === null`. `call()` encodes P with the signature types and
 * must not type the values a second time.
 */
export function sqlParamsBodyOf(sql, p) {
  if (p === null) return null;
  const out = new Out();
  out.push(T_ARRAY);
  out.u32be(2);
  tlvString(out, String(sql));
  out.append(p);
  return out.done();
}

/** The P bytes of a list of `{code, param, cell}` (`typeOf` or `typedCell`). */
function encodeTyped(types) {
  const rowBytes = rowOf(types);
  const out = new Out();
  out.push(T_ARRAY);
  out.u32be(2);
  writeHeader(out, types);
  out.push(T_BYTES);
  out.u32be(rowBytes.length);
  out.append(rowBytes);
  return out.done();
}

/** ONE typed row: the NULL bitmap, then the tagless cells. */
function rowOf(types) {
  const row = new Out();
  const bitmap = new Uint8Array(Math.ceil(types.length / 8));
  types.forEach((t, i) => { if (t.cell === null) bitmap[i >> 3] |= 1 << (i & 7); });
  row.append(bitmap);
  for (const t of types) if (t.cell !== null) t.cell(row);
  return row.done();
}

/** The header: one `Array [String "$n", IntVar code, IntVar param]` per value. */
function writeHeader(out, types) {
  out.push(T_ARRAY);
  out.u32be(types.length);
  types.forEach((t, i) => {
    out.push(T_ARRAY);
    out.u32be(3);
    tlvString(out, `$${i + 1}`);
    tlvIntVar(out, t.code);
    tlvIntVar(out, t.param);
  });
}

// ── B2173: the typed encoder of a function call ──────────────────────────────
//
// `encodeTypedParams(args, signature, fn)` writes P for `CALL fn($1, …, $n)`.
// The TYPE COMES FROM THE SIGNATURE, never from the JS value: `2` into a FLOAT
// parameter is a FLOAT, and a plain object into a ROW parameter is Json (7).
// `signature` is the `param_types` array of `SHOW FUNCTIONS`:
// `[{name, type, null}, …]`. The type spellings are the ones of the server
// (`ArgType::spell`): BOOL INT FLOAT TEXT TIMESTAMP VECTOR VECTOR(n) ROW.
//
// A NULL keeps the code of its parameter (and the dimension of a VECTOR(n)):
// the server reads the header type of a NULL cell and the bitmap says NULL.
//
// The refusal texts are the words of the server (`Signature::bind_args`), so
// a refused call reads the same in the client and on the server.

const CODE_JSON = 7;

/** The value of one argument, for the error text. */
function argKind(value) {
  if (typeof value === 'number') {
    if (Number.isNaN(value)) return 'NaN';
    if (!Number.isFinite(value)) return `the number ${value}`;
    return Number.isInteger(value) ? `the number ${value}` : `a fractional number ${value}`;
  }
  if (typeof value === 'string') return 'a string';
  if (typeof value === 'bigint') return `the bigint ${value}n`;
  if (value instanceof Date) return 'a Date';
  return kindOf(value);
}

/** A zigzag Int cell of a BigInt in the i64 range. */
function intCell(big) {
  return (out) => out.zigzag(big);
}

/** A plain object: no array, no Date, no binary view, no class instance. */
function isPlainObject(value) {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * One argument → `{code, param, cell}` for the parameter type `type`.
 * `misfit(Kind, what)` builds the refusal; `what` follows "must be …, got".
 */
function typedCell(type, value, misfit) {
  const got = () => misfit(TypeError, `got ${argKind(value)}`);
  switch (type) {
    case 'BOOL':
      if (typeof value !== 'boolean') throw got();
      return { code: CODE_BOOL, param: 0, cell: (out) => out.push(value ? 1 : 0) };
    case 'INT':
    case 'TIMESTAMP': {
      const code = type === 'INT' ? CODE_INT : CODE_TIMESTAMP;
      if (type === 'TIMESTAMP' && value instanceof Date) {
        const ms = value.getTime();
        if (!Number.isFinite(ms)) throw misfit(RangeError, 'got an invalid Date');
        return { code, param: 0, cell: intCell(BigInt(ms) * 1000n) };
      }
      if (type === 'TIMESTAMP' && typeof value === 'string') {
        const micros = isoMicros(value);
        if (micros === null) {
          throw misfit(TypeError, `got the string ${JSON.stringify(value)}, which is not an ISO 8601 time with a Z suffix`);
        }
        return { code, param: 0, cell: intCell(micros) };
      }
      if (typeof value === 'bigint') {
        if (value < I64_MIN || value > I64_MAX) {
          throw misfit(RangeError, `got ${value}n, outside the i64 range`);
        }
        return { code, param: 0, cell: intCell(value) };
      }
      if (typeof value !== 'number' || !Number.isInteger(value)) throw got();
      if (!Number.isSafeInteger(value)) {
        throw misfit(RangeError,
          `got ${value}, an integer number past ±(2^53-1) that lost its low bits: pass it as a bigint`);
      }
      return { code, param: 0, cell: intCell(BigInt(value)) };
    }
    case 'FLOAT': {
      let f = value;
      if (typeof value === 'bigint') {
        f = Number(value);
        if (BigInt(f) !== value) throw misfit(RangeError, `got ${value}n, which a FLOAT cannot hold exactly`);
      }
      if (typeof f !== 'number') throw got();
      if (!Number.isFinite(f)) throw misfit(RangeError, `got ${f}: a FLOAT must be finite`);
      return {
        code: CODE_FLOAT,
        param: 0,
        cell: (out) => {
          const b = new Uint8Array(8);
          new DataView(b.buffer).setFloat64(0, f, true);
          out.append(b);
        },
      };
    }
    case 'TEXT': {
      if (typeof value !== 'string') throw got();
      const text = UTF8.encode(value);
      return { code: CODE_TEXT, param: 0, cell: (out) => { out.uleb(BigInt(text.length)); out.append(text); } };
    }
    case 'ROW': {
      if (!isPlainObject(value)) throw got();
      let json;
      try {
        json = JSON.stringify(value);
      } catch (e) {
        throw misfit(TypeError, `got an object that is not JSON (${e?.message ?? e})`);
      }
      const bytes = UTF8.encode(json);
      return { code: CODE_JSON, param: 0, cell: (out) => { out.uleb(BigInt(bytes.length)); out.append(bytes); } };
    }
    default:
      break;
  }
  const dim = vectorDim(type);
  if (dim === null) throw misfit(TypeError, `has the type ${type}, which this client does not know`);
  let items;
  if (value instanceof Float32Array) {
    items = value;
  } else if (Array.isArray(value) && value.every((x) => typeof x === 'number')) {
    items = Float32Array.from(value);
  } else {
    throw got();
  }
  if (dim !== 0 && items.length !== dim) {
    throw misfit(RangeError, `got a vector of ${items.length} values`);
  }
  for (const f of items) {
    if (!Number.isFinite(f)) throw misfit(RangeError, 'got a vector with a value that is not a finite f32');
  }
  return {
    code: CODE_VECTOR,
    param: items.length,
    cell: (out) => {
      const b = new Uint8Array(items.length * 4);
      const view = new DataView(b.buffer);
      items.forEach((f, i) => view.setFloat32(i * 4, f, true));
      out.append(b);
    },
  };
}

/**
 * The microseconds since the epoch of an ISO 8601 UTC time
 * (`2023-11-14T22:13:20.123456Z`), or `null` for any other text. A fraction
 * past six digits is cut, because a TIMESTAMP holds microseconds.
 */
function isoMicros(text) {
  const m = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.(\d{1,9}))?Z$/.exec(text);
  if (!m) return null;
  const seconds = Date.parse(`${m[1]}Z`);
  if (!Number.isFinite(seconds)) return null;
  const fraction = BigInt((m[2] ?? '').padEnd(6, '0').slice(0, 6));
  return BigInt(seconds) * 1000n + fraction;
}

/** The dimension of `VECTOR` (0: any) or `VECTOR(n)`; `null` for another type. */
function vectorDim(type) {
  if (type === 'VECTOR') return 0;
  const m = /^VECTOR\((\d+)\)$/.exec(type);
  return m ? Number(m[1]) : null;
}

/** The header code and param of a NULL into a parameter of type `type`. */
function nullCell(type) {
  const codes = { BOOL: CODE_BOOL, INT: CODE_INT, FLOAT: CODE_FLOAT, TEXT: CODE_TEXT, TIMESTAMP: CODE_TIMESTAMP, ROW: CODE_JSON };
  if (codes[type] !== undefined) return { code: codes[type], param: 0, cell: null };
  const dim = vectorDim(type);
  if (dim !== null) return { code: CODE_VECTOR, param: dim, cell: null };
  return { code: CODE_NULL, param: 0, cell: null };
}

/**
 * The arguments in signature order. `args` is an array (`[a, b]`) or an
 * object by parameter name (`{a, b}`); `undefined` is no arguments.
 */
function argsInOrder(fn, args, signature) {
  if (args === undefined || args === null) return [];
  if (Array.isArray(args)) return args;
  if (!isPlainObject(args)) {
    throw new TypeError(`function ${fn}: pass the arguments as an array or an object by name, not ${kindOf(args)}`);
  }
  const names = new Set(signature.map((p) => p.name));
  for (const key of Object.keys(args)) {
    if (!names.has(key)) throw new TypeError(`function ${fn} has no parameter ${key}`);
  }
  return signature.map((p) => args[p.name]);
}

/**
 * B2173: the TLV bytes of P for a CALL of `fn`, typed by its signature.
 *
 * @param {Array|object|undefined} args the arguments: an array in signature
 *   order, or an object by parameter name. A missing argument of a `null:true`
 *   parameter is NULL.
 * @param {Array<{name: string, type: string, null: boolean}>} signature the
 *   `param_types` of `SHOW FUNCTIONS`.
 * @param {string} fn the function name, for the error text.
 * @returns {Uint8Array} P. A function with no parameters gives the empty P
 *   (an empty header and an empty row), as the shared vector says.
 * @throws {TypeError|RangeError} for an argument that does not fit its
 *   parameter, a missing argument, an extra argument or an unknown name. The
 *   message names the argument and the parameter.
 */
export function encodeTypedParams(args, signature, fn) {
  if (!Array.isArray(signature)) {
    throw new TypeError(`function ${fn}: the signature is not an array of parameters`);
  }
  const values = argsInOrder(fn, args, signature);
  if (values.length > signature.length) {
    throw new TypeError(`function ${fn} takes ${signature.length} arguments, got ${values.length}`);
  }
  if (signature.length > MAX_PARAMS) {
    throw new RangeError(`function ${fn}: ${signature.length} parameters, more than the limit of ${MAX_PARAMS}`);
  }
  const types = signature.map((p, i) => {
    const at = i + 1;
    const value = values[i];
    if (value === null || value === undefined) {
      if (p.null === true) return nullCell(p.type);
      if (i >= values.length) {
        throw new TypeError(`function ${fn}: argument ${at} (${p.name} ${p.type}) is missing`);
      }
      throw new TypeError(`function ${fn}: argument ${at} (${p.name}) is NOT NULL, got null`);
    }
    const misfit = (Kind, what) => new Kind(`function ${fn}: argument ${at} (${p.name}) must be ${p.type}, ${what}`);
    return typedCell(p.type, value, misfit);
  });
  return encodeTyped(types);
}

// B2174 — `transaction(statements, options)`: ONE complete transaction in ONE
// frame. The payload is the TLV
//
//   Array [ db | Null, branch | Null, token | Null, Bool forwarded, Array [group…] ]
//   group = Array [ String sql, header, IntVar nrows, Bytes rows ]
//
// `header` is the header of P above, and `rows` is `nrows` rows of P,
// concatenated. A statement without params has an empty header, `nrows` 1 and
// empty rows. Only CONSECUTIVE statements with the same text and the same
// types join one group, so the order of the statements never changes. A NULL
// value fits a column of any type: it has its bit and no cell, so the header
// takes the code of the first value that is not NULL.

/** The content type of a `POST /sql` body that carries a transaction. */
export const SQL_TXN_CONTENT_TYPE = 'application/vnd.kinedb.sql-txn';

/** The server refuses a transaction of more than this many statements. */
export const MAX_TXN_STATEMENTS = 1 << 20;

const T_NULL = 0x00;
const T_BOOL_FALSE = 0x01;

/** One statement → `{ sql, params }`, or a TypeError that names the statement. */
function statementOf(statement, n) {
  const pair = typeof statement === 'string' ? [statement] : statement;
  if (!Array.isArray(pair) || typeof pair[0] !== 'string' || pair.length > 2) {
    throw new TypeError(
      `transaction: statement ${n} is not a string, [sql] or [sql, params], but ${kindOf(statement)}`);
  }
  const params = pair[1];
  if (params !== undefined && params !== null && !Array.isArray(params)) {
    throw new TypeError(`transaction: statement ${n}: pass the values as an array, not ${kindOf(params)}`);
  }
  return { sql: pair[0], params: params ?? [] };
}

/** True when the types `t` can join the group of the types `g`. */
function fits(g, t) {
  if (g.length !== t.length) return false;
  return g.every((a, i) => a.code === CODE_NULL || t[i].code === CODE_NULL
    || (a.code === t[i].code && a.param === t[i].param));
}

/**
 * The TLV bytes of a transaction payload.
 *
 * @param {Array<string|[string]|[string, Array]>} statements in order.
 * @param {{db?: string|null, branch?: string|null, token?: string|null}} [options]
 *   `token` is the commit token (B1472). An absent value is Null.
 * @returns {Uint8Array}
 * @throws {TypeError|RangeError} for a bad statement or value. The message
 *   names the statement (1-based) and the position (`$n`).
 */
export function encodeTransactionPayload(statements, options = {}) {
  if (!Array.isArray(statements)) {
    throw new TypeError(`transaction: pass the statements as an array, not ${kindOf(statements)}`);
  }
  if (statements.length === 0) throw new TypeError('transaction: no statements');
  if (statements.length > MAX_TXN_STATEMENTS) {
    throw new RangeError(
      `transaction: ${statements.length} statements, more than the limit of ${MAX_TXN_STATEMENTS}`);
  }
  const groups = [];
  statements.forEach((statement, i) => {
    const { sql, params } = statementOf(statement, i + 1);
    let types;
    try {
      types = params.length === 0 ? [] : typesOf(params);
    } catch (e) {
      throw new e.constructor(`transaction: statement ${i + 1}: ${e.message}`);
    }
    const last = groups[groups.length - 1];
    // A statement without params never joins a group: its group is nrows 1.
    if (types.length > 0 && last && last.sql === sql && fits(last.types, types)) {
      types.forEach((t, c) => { if (last.types[c].code === CODE_NULL) last.types[c] = t; });
      last.rows.push(rowOf(types));
      return;
    }
    groups.push({ sql, types: types.slice(), rows: [rowOf(types)] });
  });

  const out = new Out();
  const option = (text) => {
    if (text === undefined || text === null) out.push(T_NULL);
    else tlvString(out, String(text));
  };
  out.push(T_ARRAY);
  out.u32be(5);
  option(options.db);
  option(options.branch);
  option(options.token);
  out.push(T_BOOL_FALSE); // `forwarded`: a client is never a peer
  out.push(T_ARRAY);
  out.u32be(groups.length);
  for (const g of groups) {
    out.push(T_ARRAY);
    out.u32be(4);
    tlvString(out, g.sql);
    writeHeader(out, g.types);
    tlvIntVar(out, g.rows.length);
    out.push(T_BYTES);
    out.u32be(g.rows.reduce((n, r) => n + r.length, 0));
    for (const r of g.rows) out.append(r);
  }
  return out.done();
}
